/**
 * De motor die de bewerkingsregels uitvoert.
 *
 * Voor één onderdeel loopt hij de regels op prioriteit af. Per regel wordt de ALS-boom
 * geëvalueerd; klopt die, dan worden de acties uitgevoerd. Een regel die
 * `activeer-bewerking` doet, komt als stap in de tomtom. Parameters die een regel zet
 * blijven staan voor de regels die erna komen — zo kan de schuurmachine de
 * fineerdikte gebruiken die de fineerstap heeft vastgelegd.
 *
 * Dit is de enige plek waar de rekenregels worden uitgevoerd. Wat de regels niet
 * zeggen, rekent de applicatie niet uit.
 */
import { AFDELINGSKLEUR, AFDELINGSTEKST } from './afdelingen.js';
import { kolomkleur } from './bewerkingskleuren.js';
import { bepaalAfwerking, kantenToeslagen, leeg, mmAf } from './opzoek.js';
import { VELDEN } from './regels.js';
const VELDPADEN = new Set(VELDEN.map((v) => v.pad));
const getal = (waarde) => {
    if (typeof waarde === 'number')
        return waarde;
    if (typeof waarde === 'boolean')
        return waarde ? 1 : 0;
    if (waarde == null || waarde === '')
        return 0;
    const n = Number(String(waarde).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};
const waar = (waarde) => {
    if (typeof waarde === 'boolean')
        return waarde;
    if (typeof waarde === 'number')
        return waarde !== 0;
    return !leeg(typeof waarde === 'string' ? waarde : '');
};
/** Nederlandse notatie: decimale komma, geen overbodige nullen. */
export const tekstVan = (waarde) => {
    if (waarde == null)
        return '';
    if (typeof waarde === 'number')
        return String(mmAf(waarde)).replace('.', ',');
    if (typeof waarde === 'boolean')
        return waarde ? 'ja' : 'nee';
    return waarde;
};
// ── Velden en tabellen opzoeken ─────────────────────────────────────────────
function veldwaarde(pad, onderdeel) {
    if (!VELDPADEN.has(pad))
        return null;
    const delen = pad.split('.').slice(1);
    let huidig = onderdeel;
    for (const deel of delen) {
        if (huidig == null || typeof huidig !== 'object')
            return null;
        huidig = huidig[deel];
    }
    if (huidig == null)
        return null;
    if (typeof huidig === 'string' || typeof huidig === 'number' || typeof huidig === 'boolean') {
        return huidig;
    }
    return null;
}
function tabelwaarde(pad, ctx) {
    const afwerking = bepaalAfwerking(ctx.onderdeel, ctx.naslag);
    switch (pad) {
        case 'Fineer.Aanwezig':
            return afwerking.heeftAfwerking;
        case 'Fineer.Dikte.Zijde0':
            return afwerking.zijde0?.dikte ?? 0;
        case 'Fineer.Dikte.Zijde5':
            return afwerking.zijde5?.dikte ?? 0;
        case 'Fineer.Dikte.Totaal':
            return afwerking.totaleFineerdikte;
        case 'Fineer.Code.Zijde0':
            return afwerking.zijde0?.materiaal ?? '';
        case 'Fineer.Code.Zijde5':
            return afwerking.zijde5?.materiaal ?? '';
        case 'Kant.Toeslag.Lengte':
            return kantenToeslagen(ctx.onderdeel, ctx.naslag).lengte;
        case 'Kant.Toeslag.Breedte':
            return kantenToeslagen(ctx.onderdeel, ctx.naslag).breedte;
        case 'Materiaal.Massief':
            return ctx.naslag.isMassief(ctx.onderdeel.materiaal);
        case 'Materiaal.VasteDikte':
            return ctx.naslag.heeftVasteDikte(ctx.onderdeel.materiaal);
        default:
            return null;
    }
}
// ── Waarden, voorwaarden en acties ──────────────────────────────────────────
export function evalueerWaarde(waarde, ctx) {
    switch (waarde.herkomst) {
        case 'veld':
            return veldwaarde(waarde.pad, ctx.onderdeel);
        case 'tabel':
            return tabelwaarde(waarde.pad, ctx);
        case 'constant':
            return waarde.waarde;
        case 'variabele':
            return ctx.parameters.get(waarde.naam) ?? null;
        case 'berekend': {
            const termen = waarde.termen.map((t) => evalueerWaarde(t, ctx));
            return rekenen(waarde.operator, termen);
        }
        case 'keuze':
            return evalueerVoorwaarde(waarde.als, ctx)
                ? evalueerWaarde(waarde.dan, ctx)
                : evalueerWaarde(waarde.anders, ctx);
    }
}
function rekenen(operator, termen) {
    const getallen = termen.map(getal);
    switch (operator) {
        case 'plus':
            return mmAf(getallen.reduce((n, v) => n + v, 0));
        case 'min':
            return mmAf(getallen.slice(1).reduce((n, v) => n - v, getallen[0] ?? 0));
        case 'maal':
            return mmAf(getallen.reduce((n, v) => n * v, 1));
        case 'gedeeld': {
            const [eerste, ...rest] = getallen;
            return mmAf(rest.reduce((n, v) => (v === 0 ? n : n / v), eerste ?? 0));
        }
        case 'minimum':
            return getallen.length === 0 ? 0 : Math.min(...getallen);
        case 'maximum':
            return getallen.length === 0 ? 0 : Math.max(...getallen);
        case 'afronden': {
            // Tweede term is het aantal decimalen; standaard hele millimeters.
            const [waarde, decimalen] = getallen;
            const factor = 10 ** Math.max(0, Math.round(decimalen ?? 0));
            return Math.round((waarde ?? 0) * factor) / factor;
        }
        default:
            return 0;
    }
}
export function evalueerVoorwaarde(voorwaarde, ctx) {
    switch (voorwaarde.soort) {
        // Een lege EN is "altijd waar": een regel zonder voorwaarden draait altijd.
        case 'en':
            return voorwaarde.kinderen.every((kind) => evalueerVoorwaarde(kind, ctx));
        // Een lege OF is "nooit waar": er is niets om aan te voldoen.
        case 'of':
            return voorwaarde.kinderen.length > 0 && voorwaarde.kinderen.some((kind) => evalueerVoorwaarde(kind, ctx));
        case 'niet':
            return !evalueerVoorwaarde(voorwaarde.kind, ctx);
        case 'vergelijking': {
            const links = evalueerWaarde(voorwaarde.links, ctx);
            const rechts = voorwaarde.rechts ? evalueerWaarde(voorwaarde.rechts, ctx) : null;
            return vergelijk(links, voorwaarde.vergelijker, rechts);
        }
    }
}
function vergelijk(links, vergelijker, rechts) {
    const tekst = (v) => String(v ?? '').trim().toUpperCase();
    switch (vergelijker) {
        case 'gelijk':
            return typeof links === 'number' || typeof rechts === 'number'
                ? getal(links) === getal(rechts)
                : tekst(links) === tekst(rechts);
        case 'ongelijk':
            return !vergelijk(links, 'gelijk', rechts);
        case 'groter':
            return getal(links) > getal(rechts);
        case 'groter-of-gelijk':
            return getal(links) >= getal(rechts);
        case 'kleiner':
            return getal(links) < getal(rechts);
        case 'kleiner-of-gelijk':
            return getal(links) <= getal(rechts);
        case 'bevat':
            return tekst(links).includes(tekst(rechts));
        // `in` vergelijkt met een lijst, komma-gescheiden ingevuld in het blokje.
        case 'in':
            return tekst(rechts)
                .split(',')
                .map((s) => s.trim())
                .includes(tekst(links));
        case 'is-gevuld':
            return !leeg(String(links ?? ''));
        case 'is-leeg':
            return leeg(String(links ?? ''));
        case 'is-waar':
            return waar(links);
    }
}
/** Vult `{parameter}` in een patroon met de waarde die op dat moment bekend is. */
export function vulPatroon(patroon, ctx) {
    return patroon.replace(/\{([\w.]+)\}/g, (_heel, naam) => {
        if (ctx.parameters.has(naam))
            return tekstVan(ctx.parameters.get(naam) ?? null);
        // Ook velden en tabelwaarden mogen in een patroon staan.
        const veld = veldwaarde(`onderdeel.${naam}`, ctx.onderdeel);
        if (veld != null)
            return tekstVan(veld);
        const tabel = tabelwaarde(naam, ctx);
        return tabel == null ? '' : tekstVan(tabel);
    });
}
// ── Eén regel toepassen ─────────────────────────────────────────────────────
/**
 * Voert één regel uit. Geeft de uitkomst terug, ook als de regel niet aansloeg —
 * dat is precies wat de editor bij "Regel testen" moet laten zien.
 */
export function pasRegelToe(regel, ctx) {
    const uitkomst = {
        regelId: regel.id,
        naam: regel.naam,
        toegepast: false,
        actief: false,
        gezet: [],
    };
    if (!regel.actief)
        return uitkomst;
    if (!evalueerVoorwaarde(regel.voorwaarde, ctx))
        return uitkomst;
    uitkomst.toegepast = true;
    for (const actie of regel.acties) {
        voerActieUit(actie, ctx, uitkomst);
    }
    return uitkomst;
}
function voerActieUit(actie, ctx, uitkomst) {
    switch (actie.soort) {
        case 'activeer-bewerking':
            uitkomst.actief = true;
            break;
        case 'parameter-instellen':
        case 'waarde-berekenen': {
            const waarde = evalueerWaarde(actie.waarde, ctx);
            ctx.parameters.set(actie.parameter, waarde);
            uitkomst.gezet.push({ parameter: actie.parameter, waarde });
            break;
        }
        case 'label-tonen':
            uitkomst.label = vulPatroon(actie.patroon, ctx);
            break;
        case 'toelichting-tonen':
            uitkomst.toelichting = vulPatroon(actie.patroon, ctx);
            break;
    }
}
// ── De hele regelset toepassen ──────────────────────────────────────────────
/**
 * Kiest per bewerking de regel die geldt: een regel voor dít bouwnummer gaat vóór de
 * algemene regel van dezelfde bewerking.
 */
export function geldendeRegels(regels, bouwnummer) {
    const perId = new Map();
    for (const regel of regels) {
        if (regel.bouwnummer != null && regel.bouwnummer !== bouwnummer)
            continue;
        const bestaand = perId.get(regel.id);
        // Bestaat er al een boot-specifieke regel, dan houdt die het.
        if (bestaand?.bouwnummer != null && regel.bouwnummer == null)
            continue;
        perId.set(regel.id, regel);
    }
    return [...perId.values()].sort((a, b) => a.prioriteit - b.prioriteit || a.id.localeCompare(b.id));
}
/**
 * Rekent één onderdeel door met de regelset: welke bewerkingen draaien er, in welke
 * volgorde, met welke waarden.
 */
export function pasRegelsToe(onderdeel, naslag, regels) {
    const ctx = { onderdeel, naslag, parameters: new Map() };
    const tomtom = [];
    const uitkomsten = [];
    for (const regel of geldendeRegels(regels, onderdeel.plaatsing.bouwnummer)) {
        const uitkomst = pasRegelToe(regel, ctx);
        uitkomsten.push(uitkomst);
        if (!uitkomst.actief)
            continue;
        // De kolomkleur komt van de bewerking (FINEER groen, CNCZ geel — de kleuren van de
        // Excel-kolomkoppen). CONTROLE en GEREED zijn geen bewerkingen maar stappen in de
        // doorloop; die houden hun afdelingskleur.
        const kleuren = kolomkleur(regel.kolomnaam) ?? {
            kleur: AFDELINGSKLEUR[regel.afdeling],
            tekst: AFDELINGSTEKST[regel.afdeling],
        };
        tomtom.push({
            sleutel: regel.id,
            soort: regel.soort,
            kolomnaam: regel.kolomnaam,
            label: uitkomst.label ?? '',
            afdeling: regel.afdeling,
            kleur: kleuren.kleur,
            tekstkleur: kleuren.tekst,
            normtijd: regel.normtijd,
            ...(uitkomst.toelichting ? { toelichting: uitkomst.toelichting } : {}),
            ...maatVan(uitkomst),
        });
    }
    return {
        tomtom,
        parameters: Object.fromEntries(ctx.parameters),
        uitkomsten,
    };
}
/**
 * De maat die bij een stap hoort, voor de schermen en de export.
 *
 * Bewust uit de parameters die déze regel heeft gezet, niet uit alles wat er op dat
 * moment bekend is: er zijn twee zaagstappen, en de tweede zou anders de maat van de
 * eerste overschrijven.
 */
function maatVan(uitkomst) {
    const gezet = new Map(uitkomst.gezet.map((g) => [g.parameter, g.waarde]));
    const maat = {};
    const lengte = gezet.get('zaagLengte');
    const breedte = gezet.get('zaagBreedte');
    const dikte = gezet.get('calibreerdikte');
    if (typeof lengte === 'number')
        maat.lengte = lengte;
    if (typeof breedte === 'number')
        maat.breedte = breedte;
    if (typeof dikte === 'number')
        maat.dikte = dikte;
    return Object.keys(maat).length > 0 ? { maat } : {};
}
//# sourceMappingURL=regelmotor.js.map